import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Link, Outlet, ScrollRestoration } from 'react-router-dom'
import { t } from '../i18n'
import { api } from '../Services/api'
import { useDiscussions } from '../State/Discussions'
import { useResource, useTrending } from '../State/refData'
import { useUi } from '../State/Ui'
import AppBar from './AppBar'
import Footer from './Footer'
import { CommentsSheet } from './Comments'
import { ChevronRight } from './Icons'
import { BottomNav, LeftNav } from './Nav'
import { ConsentBanner, QrModal, Toast } from './Overlays'
import SearchBox from './SearchBox'
import { TrendingList } from './Trending'

/* What the desktop right column shows (spec 4.3). Pages adjust it:
   - Istraži and empty "Moje teme": nothing at all (they show trending
     themselves); the column keeps its width so nothing shifts
   - a category chip on Početna: "Aktuelno · {Kategorija}"
   - Diskusije hides "Najviše komentara" (the page is that list) */
const DEFAULT_RAIL = { empty: false, search: true, category: null, discussions: true }
const RailContext = createContext(() => {})

export function useRail(config) {
  const setRail = useContext(RailContext)
  const key = JSON.stringify(config)
  useEffect(() => {
    setRail({ ...DEFAULT_RAIL, ...JSON.parse(key) })
    return () => setRail(DEFAULT_RAIL)
  }, [key, setRail])
}

/* Diskusije: the five most commented stories this week. */
function MostCommented() {
  const { enabled } = useDiscussions()
  const { openComments } = useUi()
  const { data } = useResource(
    'discussions:rail',
    () => api.discussions({ limit: 5 }).then((d) => d.items),
    { ttl: 60000, enabled }
  )
  if (!enabled || !data?.length) return null
  return (
    <section className="rail-box">
      <h2 className="section-title">{t('discussions.railTitle')}</h2>
      <ol className="topic-list">
        {data.map((story, i) => (
          <li key={story.id} className="topic-row">
            <button
              type="button"
              className="topic-row-link rail-comments-row"
              onClick={() =>
                openComments({ storyId: story.id, articleId: story.articleId, count: story.commentCount })
              }
            >
              <span className="topic-rank">{i + 1}</span>
              <span className="topic-row-text">
                <span className="discussion-row-title">{story.title}</span>
                <span className="topic-row-meta">
                  {t('discussions.comments', { count: story.commentCount })}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ol>
      <Link to="/diskusije" className="link-orange rail-more">
        {t('discussions.all')}
        <ChevronRight size={16} strokeWidth={2.5} />
      </Link>
    </section>
  )
}

function RightRail({ rail }) {
  const general = useTrending({ limit: 5, enabled: !rail.empty })
  const byCategory = useTrending({
    category: rail.category?.slug,
    limit: 5,
    enabled: !rail.empty && Boolean(rail.category),
  })
  if (rail.empty) return <aside className="right-rail" aria-hidden="true" />
  const useCategory = rail.category && byCategory.topics.length > 0
  return (
    <aside className="right-rail">
      <div className="right-rail-inner">
        {rail.search && <SearchBox />}
        <TrendingList
          topics={useCategory ? byCategory.topics : general.topics}
          title={
            useCategory
              ? t('trending.categoryTitle', { category: rail.category.title })
              : t('trending.title')
          }
        />
        {rail.discussions && <MostCommented />}
        <Footer />
      </div>
    </aside>
  )
}

export default function Layout() {
  const { isMobile } = useUi()
  const [rail, setRail] = useState(DEFAULT_RAIL)
  const railSetter = useMemo(() => setRail, [])

  return (
    <RailContext.Provider value={railSetter}>
      <a className="skip-link" href="#main">
        {t('common.home')}
      </a>
      {isMobile && <AppBar />}
      <div className="shell">
        {!isMobile && <LeftNav />}
        <main className="main-col" id="main">
          <Outlet />
        </main>
        {!isMobile && <RightRail rail={rail} />}
      </div>
      {isMobile && <BottomNav />}
      <QrModal />
      <CommentsSheet />
      <Toast />
      <ConsentBanner />
      <ScrollRestoration />
    </RailContext.Provider>
  )
}
